import { getCleanParams } from "./filtersUtils";
import store from "../store/configureStore";

const projectByIdCache = new Map();
const queryFiltersCache = new Map();
let hasResyncedDefaultProjectThisPageLoad = false;
let sessionBootstrapPromise = null;

const isFullPageLoad = () => {
    const navEntry = performance.getEntriesByType?.("navigation")?.[0];
    return navEntry?.type === "reload" || navEntry?.type === "navigate";
};

const applyUserClientLocalStorage = userResponse => {
    if (userResponse?.assetmanagement_client_id) {
        localStorage.setItem("asset_management_client", JSON.stringify(userResponse.assetmanagement_client_id));
    }
    if (userResponse?.energymanagement_client_id) {
        localStorage.setItem("energy_management_client", JSON.stringify(userResponse.energymanagement_client_id));
    }
};

const syncDefaultProjectFromServer = async ({ getUserById, updateDefaultProject, reduxDefaultProject }) => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
        return localStorage.getItem("default_project");
    }

    await getUserById(userId);
    const userResponse = store.getState().userReducer?.getUserByIdResponse;
    const serverDefaultId = userResponse?.default_project?.id;

    applyUserClientLocalStorage(userResponse);

    if (!serverDefaultId) {
        const existing = localStorage.getItem("default_project");
        if (existing && String(reduxDefaultProject || "") !== String(existing)) {
            await updateDefaultProject(existing);
        }
        return existing;
    }

    await updateDefaultProject(serverDefaultId);
    return serverDefaultId;
};

export const getSessionDefaultClient = () => {
    try {
        const assetClientRaw = localStorage.getItem("asset_management_client");
        if (assetClientRaw && assetClientRaw !== "null") {
            const assetClient = JSON.parse(assetClientRaw);
            if (assetClient?.id) {
                return assetClient;
            }
        }

        const energyClientRaw = localStorage.getItem("energy_management_client");
        if (energyClientRaw && energyClientRaw !== "null") {
            const energyClient = JSON.parse(energyClientRaw);
            if (energyClient?.id) {
                return energyClient;
            }
        }
    } catch (e) {
        return null;
    }

    return null;
};

export const getSessionDefaultClientId = () => getSessionDefaultClient()?.id || null;

export const resolveDefaultClientFromList = (clientList = []) => {
    const sessionClientId = getSessionDefaultClientId();
    if (!sessionClientId) {
        return null;
    }

    return clientList.find(client => String(client.id) === String(sessionClientId)) || { id: sessionClientId };
};

export const setSessionDefaultClients = client => {
    if (!client?.id) {
        return;
    }

    localStorage.setItem("asset_management_client", JSON.stringify(client));
    localStorage.setItem("energy_management_client", JSON.stringify(client));
};

export const getSessionDefaultProjectId = reduxDefaultProject =>
    reduxDefaultProject || localStorage.getItem("default_project") || store.getState().dashboardReducer?.defaultProject || null;

export const resyncSessionDefaultProjectFromServer = async ({ getUserById, updateDefaultProject, reduxDefaultProject }) =>
    syncDefaultProjectFromServer({ getUserById, updateDefaultProject, reduxDefaultProject });

export const resolveSessionDefaultProject = async ({ getUserById, updateDefaultProject, reduxDefaultProject }) => {
    if (sessionBootstrapPromise) {
        return sessionBootstrapPromise;
    }

    sessionBootstrapPromise = (async () => {
        const existing = localStorage.getItem("default_project");
        const shouldResyncFromServer = isFullPageLoad() && !hasResyncedDefaultProjectThisPageLoad;

        if (shouldResyncFromServer) {
            hasResyncedDefaultProjectThisPageLoad = true;
            return syncDefaultProjectFromServer({ getUserById, updateDefaultProject, reduxDefaultProject });
        }

        if (existing) {
            if (String(reduxDefaultProject || "") !== String(existing)) {
                await updateDefaultProject(existing);
            }
            return existing;
        }

        hasResyncedDefaultProjectThisPageLoad = true;
        return syncDefaultProjectFromServer({ getUserById, updateDefaultProject, reduxDefaultProject });
    })().finally(() => {
        sessionBootstrapPromise = null;
    });

    return sessionBootstrapPromise;
};

export const getEffectiveProjectIds = (filterParams, sessionDefaultProjectId) => {
    if (filterParams?.project_ids?.length) {
        return filterParams.project_ids;
    }

    const sessionDefault = sessionDefaultProjectId || getSessionDefaultProjectId();
    return sessionDefault ? [sessionDefault] : [];
};

export const getDashboardParamsWithBuildingTypeIds = (params, masterBuildingTypes = []) => {
    const buildingTypes = params?.building_types;
    if (!buildingTypes?.length) {
        return params;
    }

    const buildingTypeIds = masterBuildingTypes
        .filter(item => buildingTypes.some(value => String(value) === String(item.id) || String(value) === String(item.name)))
        .map(item => item.id);

    return buildingTypeIds.length ? { ...params, building_types: buildingTypeIds } : params;
};

export const buildDashboardReportParams = (params, { masterBuildingTypes = [], sessionDefaultProjectId } = {}, isAssetDashboard = false) => {
    const reportFilters = getDashboardParamsWithBuildingTypeIds(getCleanParams(params, "dashboard"), masterBuildingTypes);
    const projectIds = getEffectiveProjectIds(reportFilters, sessionDefaultProjectId);

    return {
        ...reportFilters,
        ...(projectIds.length && !isAssetDashboard ? { project_ids: projectIds } : {}),
        chart_type: isAssetDashboard ? "assetDashboard" : "dashboard",
        user_id: localStorage.getItem("userId")
    };
};

export const getCachedProject = projectId => {
    if (!projectId) {
        return null;
    }
    return projectByIdCache.get(String(projectId)) || null;
};

export const setCachedProject = (projectId, data) => {
    if (projectId && data) {
        projectByIdCache.set(String(projectId), data);
    }
};

const getQueryCacheKey = (clientId, projectId, type) => `${clientId}:${projectId}:${type || "initial"}`;

export const getCachedQueryFilters = (clientId, projectId, type) => {
    if (!clientId || !projectId) {
        return null;
    }
    return queryFiltersCache.get(getQueryCacheKey(clientId, projectId, type)) ?? null;
};

export const setCachedQueryFilters = (clientId, projectId, type, queries) => {
    if (!clientId || !projectId) {
        return;
    }
    queryFiltersCache.set(getQueryCacheKey(clientId, projectId, type), queries);
};

let queryCreatedUserFilterPreference = null;

export const getQueryCreatedUserFilterPreference = (projectId, isSavedViews) => {
    if (!projectId || !queryCreatedUserFilterPreference) {
        return null;
    }
    const scope = isSavedViews ? "views" : "filters";
    if (
        String(queryCreatedUserFilterPreference.projectId) !== String(projectId) ||
        queryCreatedUserFilterPreference.scope !== scope
    ) {
        return null;
    }
    return Array.isArray(queryCreatedUserFilterPreference.ids)
        ? queryCreatedUserFilterPreference.ids.map(id => String(id))
        : null;
};

export const setQueryCreatedUserFilterPreference = (projectId, isSavedViews, ids) => {
    if (!projectId) {
        return;
    }
    queryCreatedUserFilterPreference = {
        projectId: String(projectId),
        scope: isSavedViews ? "views" : "filters",
        ids: (ids || []).map(id => String(id))
    };
};

export const clearQueryCreatedUserFilterPreference = () => {
    queryCreatedUserFilterPreference = null;
};

export const invalidateDashboardSessionCache = ({ previousProjectId } = {}) => {
    queryFiltersCache.clear();
    clearQueryCreatedUserFilterPreference();
    if (previousProjectId) {
        projectByIdCache.delete(String(previousProjectId));
    }
    sessionStorage.removeItem("assetDashboardMapState");
};

export const invalidateQueryFiltersForProject = projectId => {
    if (!projectId) {
        return;
    }
    const projectKey = `:${projectId}:`;
    for (const key of queryFiltersCache.keys()) {
        if (key.includes(projectKey)) {
            queryFiltersCache.delete(key);
        }
    }
};

export const clearSelectedChartMetadata = dashboardInstance => {
    if (dashboardInstance) {
        dashboardInstance.selectedChartMetadata = {};
    }
};
